import { useContext } from 'react';
import { useSelector } from 'react-redux';
import Stripe from 'stripe';

import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import {
  getStudioPlanRank,
  getStudioPlanTitle,
  planHasFeatureAccess,
  requiredPlanForFeature,
} from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import type { StudioFeature } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import { useGetCurrentPlanQuery } from 'src/services/api/studioCreditApi';
import type { StudioPlanId } from 'src/services/api/studioCreditApi';
import { RootState } from 'src/store';
import * as PlansUtils from 'src/utils/PlansUtils';

/**
 * Studio pricing entitlement layer exposed alongside the Classic plan status
 * (POR-21135). All Studio surface gates and upgrade badges read from
 * `usePlanStatus().studio` so the PRD matrix lives in exactly one place. Inert
 * on Classic workspaces or when the `studio-pricing` flag is off, in which case
 * it imposes no locks — the Classic gating path is preserved unchanged.
 */
export interface StudioEntitlements {
  /** Studio pricing flag is on and this is a Studio workspace. */
  isStudioPricingWorkspace: boolean;
  /**
   * True while the current-plan query is loading on a Studio pricing workspace.
   * Entitlements are unresolved until this clears — the plan reads as the
   * freemium baseline meanwhile — so gates should defer their lock UI
   * (spinner/skeleton) rather than flash upgrade prompts.
   */
  isCurrentPlanLoading: boolean;
  /**
   * Resolved Studio plan. Null only when inert (Classic / flag-off); on a
   * Studio pricing workspace this is at least `freemium`, never null.
   */
  studioPlan: StudioPlanId | null;
  /** Rank of {@link studioPlan}, or -1 when inert. */
  studioPlanRank: number;
  /** Whether the current plan unlocks a feature (always true when inert). */
  hasFeatureAccess: (feature: StudioFeature) => boolean;
  /** Lowest plan that unlocks a feature, regardless of the current plan. */
  requiredPlanFor: (feature: StudioFeature) => StudioPlanId;
  /** Required-plan label for a locked feature, or null when unlocked/inert. */
  upgradeBadgeLabel: (feature: StudioFeature) => string | null;
}

/**
 *
 * @returns information regarding current user plan.
 * @note - all plans other than ACTIVE_BILLING_PLANS are now considered legacy plans
 */
export const usePlanStatus = () => {
  const isClient = useSelector((state: RootState) => state.user.isClient);

  const {
    stripeCustomer,
    allSubscriptions,
    customerSubscription,
    customerSubscriptions,
  } = useCustomerSubscription();

  const planStatus: {
    isProPlan: boolean;
    isBasicPlan: boolean;
    isFixedAccessProPlan: boolean;
    isFixedAccessBasicPlan: boolean;
    isTrialing: boolean;
    isTrialPeriodExpired: boolean;
    requireProUpgrade: boolean;
    requireAdvancedUpgrade: boolean;
    isPaidPlan: boolean;
    isCancelledPlan: boolean;
    isStarterPlan: boolean;
    isCopilotProfessionalPlan: boolean;
    isAdvancedPlan: boolean;
    isEnterprisePlan: boolean;
    billingCycle: Stripe.Price.Recurring.Interval | undefined;
    stripeCustomer: Stripe.Customer | undefined;
  } = {
    isProPlan: false,
    isBasicPlan: false,
    isFixedAccessProPlan: false,
    isFixedAccessBasicPlan: false,
    // isTrialing indicates if at current moment user is on trial or not
    isTrialing: false,
    // checks that user is on trial or on basic plan
    requireProUpgrade: false,
    // builds on top of requireProUpgrade to also check if user is on starter plan
    // or pro plan
    requireAdvancedUpgrade: false,
    // isTrialPeriodExpired indicates if trial got expired or not
    // is true even if user is on paid plan but the initial offered trial got expired
    isTrialPeriodExpired: false,
    // isCancelledPlan indicates if paid subscription is cancelled because of any reason (manual cancellation or non-payment)
    isCancelledPlan: false,
    // isPaidPlan user has an active subscription and not currently trialing
    isPaidPlan: false,
    // Current active low tier plan.
    isStarterPlan: false,
    // Current active professional plan. Note that it's different from legacy pro plan.
    isCopilotProfessionalPlan: false,
    // Currently active advanced plan.
    isAdvancedPlan: false,
    // Currently active enterprise plan.
    isEnterprisePlan: false,
    billingCycle: undefined,
    stripeCustomer: stripeCustomer,
  };

  if (stripeCustomer && !isClient) {
    planStatus.isTrialing = PlansUtils.hasActiveTrial(allSubscriptions);
    planStatus.isTrialPeriodExpired =
      PlansUtils.isTrialPeriodExpired(allSubscriptions);

    // isCancelledPlan indicates if subscription is cancelled because of any reason
    planStatus.isCancelledPlan =
      PlansUtils.hasCancelledPlan(allSubscriptions) && !customerSubscription;

    const activeSubscriptionPlanPrice =
      customerSubscription?.items?.data[0].price;

    planStatus.billingCycle = PlansUtils.getBillingCycle(
      activeSubscriptionPlanPrice,
    );
    planStatus.isProPlan =
      PlansUtils.isProPlan(activeSubscriptionPlanPrice) &&
      !planStatus.isCancelledPlan;
    planStatus.isBasicPlan =
      PlansUtils.isBasicPlan(activeSubscriptionPlanPrice) &&
      !planStatus.isCancelledPlan;
    planStatus.isFixedAccessBasicPlan = PlansUtils.isFixedAccessBasicPlan(
      activeSubscriptionPlanPrice,
    );
    planStatus.isFixedAccessProPlan = PlansUtils.isFixedAccessProPlan(
      activeSubscriptionPlanPrice,
    );

    planStatus.isStarterPlan = PlansUtils.isStarterPlan(
      activeSubscriptionPlanPrice,
    );

    planStatus.isCopilotProfessionalPlan = PlansUtils.isCopilotProfessionalPlan(
      activeSubscriptionPlanPrice,
    );

    planStatus.isAdvancedPlan = PlansUtils.isAdvancedPlan(
      activeSubscriptionPlanPrice,
    );

    planStatus.isEnterprisePlan = PlansUtils.isEnterprisePlan(
      activeSubscriptionPlanPrice,
    );

    planStatus.requireProUpgrade =
      planStatus.isTrialing || planStatus.isBasicPlan;

    planStatus.requireAdvancedUpgrade =
      (planStatus.requireProUpgrade ||
        planStatus.isStarterPlan ||
        planStatus.isProPlan) &&
      // also check if plan is not an enterprise plan
      // because for enterprise plans we use the plan_status
      // pro_xxx_0_ent along with advanced_xxx_0_ent if we check
      // for the isProPlan only to determine if advanced upgrade is required
      // then it will return true for enterprise plans as well unless we check
      // explicitly if th plan is not an enterprise plan
      !PlansUtils.isEnterprisePlan(activeSubscriptionPlanPrice);

    planStatus.isPaidPlan =
      planStatus.isProPlan ||
      planStatus.isBasicPlan ||
      planStatus.isFixedAccessBasicPlan ||
      planStatus.isFixedAccessProPlan ||
      planStatus.isStarterPlan ||
      planStatus.isCopilotProfessionalPlan ||
      planStatus.isAdvancedPlan ||
      planStatus.isEnterprisePlan;
  }

  // ── Studio pricing entitlements ────────────────────────────────────────────
  // Additive and self-contained: this section never reads from or writes to the
  // Classic `planStatus` booleans above, so Classic behavior is unchanged. It is
  // inert (never locks) on Classic workspaces or when the studio-pricing flag is
  // off. See {@link StudioEntitlements}.
  const { EnableStudioPricing } = useFeatureFlags();
  // Read isStudioUser from portal config rather than useIsStudioUser(), which
  // also requires the FTUX flag — that must not gate pricing entitlements.
  const { isStudioUser } = useContext(PortalConfigContext);
  // A live subscription on a Studio base plan also marks a Studio pricing
  // workspace: billing keys on the subscription, not the creation-time
  // isStudioUser marker (mirroring ParseStudioSubscription on the backend), so
  // a Classic-origin workspace moved onto a Studio price must resolve Studio
  // entitlements rather than falling through to Classic price-ID gating, which
  // can never match an anonymous Studio price.
  const isStudioPricingWorkspace =
    EnableStudioPricing &&
    (Boolean(isStudioUser) ||
      customerSubscriptions.some(PlansUtils.isStudioPlanSubscription));

  // Skip the request on Classic / flag-off workspaces so the hook adds no
  // network cost there.
  const { data: currentPlan, isLoading } = useGetCurrentPlanQuery(undefined, {
    skip: !isStudioPricingWorkspace,
  });

  // isLoading is the first-load signal (no data yet); it stays false when the
  // query is skipped, so this is false on inert workspaces.
  const isCurrentPlanLoading = isStudioPricingWorkspace && isLoading;

  // Inert workspaces (Classic / flag-off) stay null so the Classic gating path —
  // not this section — governs their access. A Studio pricing workspace always
  // sits on at least the freemium baseline: only a resolved Studio-family
  // subscription lifts it higher, so unresolved states (in-flight query, request
  // error, or a `none`/Classic response) fall back to freemium and fail closed
  // rather than exposing paid features.
  const studioPlan: StudioPlanId | null = !isStudioPricingWorkspace
    ? null
    : currentPlan?.family === 'studio'
      ? (currentPlan.plan ?? 'freemium')
      : 'freemium';

  const hasFeatureAccess = (feature: StudioFeature): boolean =>
    studioPlan === null ? true : planHasFeatureAccess(studioPlan, feature);

  const upgradeBadgeLabel = (feature: StudioFeature): string | null => {
    if (studioPlan === null || planHasFeatureAccess(studioPlan, feature)) {
      return null;
    }
    // Reuse the plans-page title source so a plan renders one label everywhere.
    return getStudioPlanTitle(requiredPlanForFeature(feature));
  };

  const studio: StudioEntitlements = {
    isStudioPricingWorkspace,
    isCurrentPlanLoading,
    studioPlan,
    studioPlanRank: studioPlan === null ? -1 : getStudioPlanRank(studioPlan),
    hasFeatureAccess,
    requiredPlanFor: requiredPlanForFeature,
    upgradeBadgeLabel,
  };

  return { ...planStatus, studio };
};
